// ClientContext.tsx
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type ClientContextType = {
  name: string;
  organization: string;
  isAdmin: boolean;
  expirationDate: Date | null;
  subscriptionIsActive: boolean;
  setClientInfo: (
    info: Partial<{ name: string; organization: string; isAdmin: boolean; expirationDate: Date | null; subscriptionIsActive: boolean }>
  ) => void;
};

const defaultContext: ClientContextType = {
  name: '',
  organization: '',
  isAdmin: false,
  expirationDate: null,
  subscriptionIsActive: false,
  setClientInfo: () => { },
};

export const ClientContext = createContext<ClientContextType>(defaultContext);
export const useClient = () => useContext(ClientContext);

export const ClientContextProvider = ({ children }: { children: ReactNode }) => {
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [subscriptionIsActive, setSubscriptionIsActive] = useState<boolean>(false);

  // Load from localStorage on first render
  useEffect(() => {
    const storedName = localStorage.getItem('clientName');
    const storedOrg = localStorage.getItem('clientOrganization');
    const storedIsAdmin = localStorage.getItem('clientIsAdmin') === 'true';
    const storedExpiration = localStorage.getItem('clientExpirationDate');
    const storedSubscriptionIsActive = localStorage.getItem('subscriptionIsActive') === 'true';

    if (storedName) setName(storedName);
    if (storedOrg) setOrganization(storedOrg);
    if (storedIsAdmin) setIsAdmin(storedIsAdmin);
    if (storedExpiration && storedExpiration !== '') {
      const parsed = new Date(storedExpiration);
      setExpirationDate(!isNaN(parsed.getTime()) ? parsed : null);
    }
    if (storedSubscriptionIsActive) setSubscriptionIsActive(storedSubscriptionIsActive);
  }, []);

  const setClientInfo = ({
    name,
    organization,
    isAdmin,
    expirationDate,
    subscriptionIsActive,
  }: Partial<{ name: string; organization: string; isAdmin: boolean; expirationDate: Date | null; subscriptionIsActive: boolean }>) => {
    if (name !== undefined) {
      setName(name);
      localStorage.setItem('clientName', name);
    }
    if (organization !== undefined) {
      setOrganization(organization);
      localStorage.setItem('clientOrganization', organization);
    }
    if (isAdmin !== undefined) {
      setIsAdmin(isAdmin);
      localStorage.setItem('clientIsAdmin', isAdmin ? 'true' : 'false');
    }
    if (expirationDate !== undefined) {
      setExpirationDate(expirationDate);
      localStorage.setItem('clientExpirationDate', (expirationDate && expirationDate instanceof Date && !isNaN(expirationDate.getTime())) ? expirationDate.toISOString() : '');
    }
    if (subscriptionIsActive !== undefined) {
      setSubscriptionIsActive(subscriptionIsActive);
      localStorage.setItem('subscriptionIsActive', subscriptionIsActive ? 'true' : 'false');
    }
  };

  return (
    <ClientContext.Provider value={{ name, organization, isAdmin, expirationDate, subscriptionIsActive, setClientInfo }}>
      {children}
    </ClientContext.Provider>
  );
};